import React, { useState, useRef, useCallback, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { Amplify, Auth } from "aws-amplify";
import awsExports from "./aws-exports";
import ChatPane from "./components/ChatPane";
import PreviewPane from "./components/PreviewPane";
import Sidebar from "./components/Sidebar";
import CmsLoginModal from "./components/CmsLoginModal";
import TenantSelectorModal from "./components/TenantSelectorModal";
import TenantDropdown from "./components/TenantDropdown";
import PublishWizard from "./components/PublishWizard";
import DuplicateAppModal from "./components/DuplicateAppModal";
import DeviceDashboard from "./components/DeviceDashboard";
import HistoryPage from "./components/HistoryPage";
import AppsPage from "./components/AppsPage";
import WebhooksPage from "./components/WebhooksPage";
import IntegrationsList from "./components/IntegrationsList";
import PlayerPage from "./components/PlayerPage";
import PromptLogsPage from "./components/PromptLogsPage";
import UsagePage from "./components/UsagePage";
import ApiKeysEditor from "./components/ApiKeysEditor";
import AppVarsEditor from "./components/AppVarsEditor";
import VersionHistory from "./components/VersionHistory";
import useIntegrations from "./hooks/useIntegrations";
import { streamChat } from "./streamChat";
import { authFetch, API_BASE, extractError, decomposeEdit, inferContract } from "./api";
import { isComplexCreation, shouldDecompose, wantsToPreserveDesign, buildPassMessage, FixPlanCard, BuildPlanCard } from "./components/SmartEditFlow";
import { extractQuestions } from "./components/DesignQuestionsCard";
import { canSave, canPublish } from "./lib/actionGates";
import "./App.css";

Amplify.configure(awsExports);

// Re-root a backend-supplied preview URL to the current browser origin so that
// accessing Forge from a non-localhost address (e.g. a LAN IP) still produces a
// working iframe URL. The backend embeds its own origin (e.g. localhost:8443),
// which the browser interprets as the client machine → "refused to connect".
function localizePreviewUrl(url) {
  if (!url) return url;
  try {
    const parsed = new URL(url);
    // S3/CloudFront URLs (e.g. /previews/...) should be used as-is
    if (!parsed.pathname.startsWith('/apps/')) return url;
    const base = process.env.REACT_APP_PREVIEW_BASE_URL
      ? new URL(process.env.REACT_APP_PREVIEW_BASE_URL).origin
      : window.location.origin;
    return base + parsed.pathname + parsed.search;
  } catch {
    return url;
  }
}

// Timing constants
const PREVIEW_RELOAD_DELAY = 2000;
const ERROR_CHECK_DELAY = 12000; // Wait 12s for container to start + complete first API calls before validating
const HEALTH_CHECK_DELAY = 5000;

function sanitizeName(name) {
  return name.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "") || "integration";
}

// Fingerprint + history-shaping helpers live in a pure-JS lib file so the
// regression tests can import them without pulling in the full React tree
// (react-markdown is ESM-only and breaks jest's CJS loader).
import { computeValidationSignature, buildHistoryWithWaterline } from "./lib/historyHelpers";

// Build a code context string from the current payload for edit prompts.
// This lets Claude see the existing code so it can make targeted changes
// instead of regenerating everything from scratch.
//
// For DESIGN-layer edits, the full routes/data.js implementation (fetch
// plumbing, caching, retry logic — often 10-15K chars) is replaced with
// a compact "data shape" block derived from the running container's
// live /data response. This recovers ~40% of the token budget for apps
// with heavy data layers (e.g. apple-music-dashboard at 30K HTML + 15K
// data.js was causing Claude Opus 4.7 to silently return 0 chars because
// thinking budget + context exhausted before generation started). Claude
// still gets the data shape + sample values it needs to design against;
// it just doesn't see the fetch implementation it can't modify anyway.
//
// Pass { layer: "design", sampleData } to enable the swap. Without
// sampleData (e.g., inferContract failed), falls back to the full code
// so we never regress design quality when the trim isn't safe.
function buildCodeContext(payload, opts = {}) {
  if (!payload) return null;
  if (payload.format === "scaffold" && payload.files) {
    const { layer, sampleData } = opts;
    const shouldTrimData = layer === "design" && sampleData && typeof sampleData === "object";

    return Object.entries(payload.files)
      .map(([p, code]) => {
        // For design edits, swap routes/data.js + routes/sources/*.js for
        // a data-shape block. The HTML still sees the exact values it
        // will render against.
        if (shouldTrimData && (p === "routes/data.js" || p.startsWith("routes/sources/"))) {
          // Only emit ONCE — collapse the whole data layer into a single
          // shape block placed at the first data-file encounter, and skip
          // subsequent ones.
          if (p !== "routes/data.js" && !Object.keys(payload.files).includes("routes/data.js")) {
            // Edge case: multi-source app with no classic routes/data.js.
            // Keep emitting only the FIRST source file's shape replacement.
            const sourceFiles = Object.keys(payload.files).filter(f => f.startsWith("routes/sources/"));
            if (sourceFiles[0] !== p) return null;
          } else if (p !== "routes/data.js") {
            return null;
          }
          return (
            `[DATA SHAPE — live sample of what GET /data returns]\n` +
            `\`\`\`json\n${JSON.stringify(sampleData, null, 2)}\n\`\`\`\n` +
            `Note: this pass only touches views/index.html. The data-layer ` +
            `implementation (fetching, caching, proxy plumbing) is not shown — ` +
            `reference the shape above when designing against the real data.`
          );
        }
        return `[FILE: ${p}]\n\`\`\`${p.endsWith(".html") ? "html" : "javascript"}\n${code}\n\`\`\``;
      })
      .filter(Boolean)
      .join("\n\n");
  }
  if (payload.code) return `\`\`\`javascript\n${payload.code}\n\`\`\``;
  return null;
}

export default function App() {
  const [sessionId, setSessionId] = useState(() => uuidv4());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  // pendingPayload: { format: 'scaffold', files: {...} } or { format: 'monolithic', code: '...' } or null
  const [pendingPayload, setPendingPayload] = useState(null);
  const pendingPayloadRef = useRef(null);
  // Keep ref in sync with state (ref used inside streamAndDeploy to avoid stale closures)
  useEffect(() => { pendingPayloadRef.current = pendingPayload; }, [pendingPayload]);
  const [deploying, setDeploying] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [integrationName, setIntegrationName] = useState(null);
  const integrationNameRef = useRef(null);
  const [page, setPage] = useState("home");
  // When a classifier-linked error fires, clicking "See setup guide →" sets
  // this; IntegrationsList auto-opens the recipe and scrolls to the anchor.
  // Cleared when IntegrationsList finishes consuming it.
  const [pendingRecipeNav, setPendingRecipeNav] = useState(null);
  const [appVersion, setAppVersion] = useState(null);
  const [isSaved, setIsSaved] = useState(false); // Track whether current payload has been promoted
  // Self-healing: track auto-fix iterations per deploy cycle
  const [healIterations, setHealIterations] = useState(0);
  const healIterationsRef = useRef(0);
  useEffect(() => { healIterationsRef.current = healIterations; }, [healIterations]);
  const MAX_HEAL_ITERATIONS = 3;
  // Signature of the validation result we LAST fired auto-fix against. If the
  // next validation comes back with the same signature, we know the previous
  // fix attempt produced zero progress (same errors, same classifier category)
  // and another Claude call is just going to produce the same no-op. The user
  // feedback log from Rob's Now-Playing-2 session showed this running 4 times
  // against a container that was transiently UNHEALTHY but functionally fine.
  const lastAutoFixSignatureRef = useRef(null);
  const previewRef = useRef(null);
  // Ref for validateAndAutoFix — allows streamAndDeploy (early) to call it without circular dependency
  const validateAndAutoFixRef = useRef(null);

  // Auth state
  const [forgeUser, setForgeUser] = useState(null); // { email } — set after Cognito session confirmed
  const [authLoading, setAuthLoading] = useState(true); // true while restoring session on mount
  const [cmsAuth, setCmsAuth] = useState(() => {
    try { const s = localStorage.getItem("videri-forge-cms"); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [cmsTenants, setCmsTenants] = useState([]); // all tenants for current user
  const [pendingTenants, setPendingTenants] = useState(null); // non-null when tenant selector should show
  const [publishTarget, setPublishTarget] = useState(null);
  const [duplicateSource, setDuplicateSource] = useState(null);
  const [showKeysEditor, setShowKeysEditor] = useState(false);
  const [showVarsEditor, setShowVarsEditor] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // Restore Cognito session on mount
  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((cognitoUser) => completeLogin(cognitoUser))
      .catch(() => setAuthLoading(false));
  }, []); // eslint-disable-line

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem("forge-sidebar-open");
    return saved !== null ? saved === "true" : true;
  });
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => {
      localStorage.setItem("forge-sidebar-open", String(!prev));
      return !prev;
    });
  }, []);

  // Integrations (shared with sidebar) — only poll when authenticated
  const { integrations, loading: integrationsLoading, actionLoading: igActionLoading, doAction: igDoAction, fetchIntegrations: refetchIntegrations } = useIntegrations(!!forgeUser);

  // Fetch app version from manifest
  const fetchAppVersion = useCallback(async (name) => {
    if (!name) { setAppVersion(null); return; }
    try {
      const res = await authFetch(`${API_BASE}/apps/${name}/manifest`);
      const data = await res.json();
      setAppVersion(data.version || 0);
    } catch { setAppVersion(null); }
  }, []);

  // Refresh version when integration name changes
  useEffect(() => {
    fetchAppVersion(integrationName);
  }, [integrationName, fetchAppVersion]);

  // Reset to fresh chat
  const resetChat = useCallback(() => {
    // Clean up preview container if one exists
    if (integrationNameRef.current) {
      authFetch(`${API_BASE}/deploy/cleanup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: integrationNameRef.current }),
      }).catch(() => {});
    }
    setMessages([]);
    setInput("");
    setPendingPayload(null);
    setIsSaved(false);
    setPreviewUrl(null);
    setIntegrationName(null);
    setAppVersion(null);
    integrationNameRef.current = null;
    setSessionId(uuidv4());
    setLoading(false);
    setDeploying(false);
  }, []);

  /**
   * Assign an app name, checking for collisions with existing integrations.
   * If the name is already taken, appends a numeric suffix (e.g., "fifa-world-cup-2").
   * Sets integrationNameRef and integrationName state.
   * Returns the final (possibly deduplicated) name.
   */
  const assignAppName = useCallback((suggestedName) => {
    if (integrationNameRef.current) return integrationNameRef.current; // Already named

    let name = sanitizeName(suggestedName);
    const existingNames = new Set((integrations || []).map((ig) => ig.name));

    if (existingNames.has(name)) {
      // Find next available suffix
      let suffix = 2;
      while (existingNames.has(`${name}-${suffix}`)) suffix++;
      const deduped = `${name}-${suffix}`;
      console.log(`[assignAppName] "${name}" already exists, using "${deduped}"`);
      name = deduped;
    }

    integrationNameRef.current = name;
    setIntegrationName(name);
    return name;
  }, [integrations]);

  /**
   * Shared helper: stream a chat response, update messages, auto-deploy preview if code generated.
   * Used by sendMessage, sendDirect, and autoFix to eliminate duplicate streaming logic.
   */
  const streamAndDeploy = useCallback(async ({ message, history, attachment, skipPreview, isEdit, isAutoFix, layer, skipRecipeMatch, userPrompt }) => {
    // Add streaming assistant message placeholder
    setMessages((prev) => [...prev, { role: "assistant", content: "", _streaming: true }]);

    try {
      let result = await streamChat({
        sessionId,
        message,
        history,
        integrationName: integrationNameRef.current,
        attachment,
        isEdit: isEdit || false,
        isAutoFix: isAutoFix || false,
        skipRecipeMatch: skipRecipeMatch || false,
        layer,
        onText: (text) => {
          setMessages((prev) => {
            const u = [...prev];
            u[u.length - 1] = { role: "assistant", content: text, _streaming: true };
            return u;
          });
        },
        onName: (name) => {
          assignAppName(name);
        },
      });

      // ── Recipe suggestion short-circuit ────────────────────────────
      // The backend matched this prompt to a curated recipe and returned
      // without running Claude. Replace the streaming placeholder with a
      // recipe-suggestion card; the user picks Use Recipe or Continue with AI.
      if (result.recipeSuggestion) {
        setMessages((prev) => {
          const u = [...prev];
          u[u.length - 1] = {
            role: "system",
            content: "",
            _isRecipeSuggestion: true,
            _recipeMatch: result.recipeSuggestion,
            // Preserve what we need to re-issue the prompt if the user picks Continue
            _originalMessage: message,
            _originalHistory: history,
            _originalAttachment: attachment,
            _originalIsEdit: isEdit,
            _originalIsAutoFix: isAutoFix,
            _originalLayer: layer,
          };
          return u;
        });
        return;
      }

      // ── Dead-end refusal auto-retry ─────────────────────────────────
      // Claude occasionally plays a scoped-agent role it wasn't asked to play,
      // refusing the user and pointing at a "different panel" that doesn't
      // exist. Rob's "Now Playing 2" session hit this twice. The updated
      // prompts discourage the behavior, but when it slips through anyway
      // we detect the signature and retry ONCE with scope explicitly cleared
      // so the user isn't left at a dead end.
      //
      // Detection: the response is pure prose (no files), short-ish, AND
      // contains one of a handful of tell-tale phrases. False-positive risk
      // is low because each phrase is specific enough that genuine helpful
      // responses don't use them.
      const DEAD_END_PATTERNS = [
        /i'?m\s+the\s+(data|design)\s+layer\s+assistant/i,
        /could\s+you\s+resend\s+your\s+request/i,
        /please\s+resend\s+your\s+(request|message)/i,
        /not\s+seeing\s+(the|your)\s+.{0,40}\s*code/i,
        /(make|try|send)\s+this\s+(request|change)\s+(in|at)\s+the\s+[\w/-]*\s*(view|design|data)\s*[\w/-]*\s*(panel|tab|editor)/i,
        /handled\s+by\s+a\s+different\s+part\s+of\s+forge/i,
      ];
      const initialText = result.fullText || "";
      const initialHasFiles = !!(result.extractedFiles || result.extractedCode);
      const looksLikeDeadEnd =
        !initialHasFiles &&
        initialText.length < 2000 &&
        DEAD_END_PATTERNS.some((re) => re.test(initialText));

      if (looksLikeDeadEnd) {
        console.warn("[streamAndDeploy] Dead-end response detected, retrying once without scope");
        setMessages((prev) => {
          const u = [...prev];
          u[u.length - 1] = { role: "assistant", content: "", _streaming: true };
          return u;
        });
        try {
          result = await streamChat({
            sessionId,
            message,
            history,
            integrationName: integrationNameRef.current,
            attachment,
            isEdit: isEdit || false,
            // Scope is intentionally cleared on the retry — we suspect it
            // caused the dead-end. Also set skipRecipeMatch + isAutoFix so
            // the recipe matcher doesn't kick in and short-circuit again.
            isAutoFix: true,
            skipRecipeMatch: true,
            // layer intentionally omitted
            onText: (text) => {
              setMessages((prev) => {
                const u = [...prev];
                u[u.length - 1] = { role: "assistant", content: text, _streaming: true };
                return u;
              });
            },
            onName: (name) => {
              assignAppName(name);
            },
          });
        } catch (retryErr) {
          // If the retry itself fails, bail to the normal error-handling flow
          console.warn("[streamAndDeploy] Retry failed, falling through:", retryErr.message);
          throw retryErr;
        }
      }

      const { fullText, extractedCode, extractedFiles, changeSummary, iterationId } = result;

      // Build the payload — scaffold (files) takes priority over monolithic (code).
      // DEFENSE: if the current app is already a scaffold, reject monolithic
      // `extractedCode` outright — it's almost certainly the backend's fallback
      // regex misfiring on an illustrative (bash/curl) block in a text-only
      // response. Deploying it as server.js would overwrite a working scaffold
      // with arbitrary garbage.
      const prevForGuard = pendingPayloadRef.current;
      const isScaffoldApp = prevForGuard?.format === "scaffold";
      const hasCode = extractedFiles || (extractedCode && !isScaffoldApp);
      let payload;
      if (extractedFiles) {
        // Merge with previously deployed scaffold files (for partial edits)
        const previousFiles = prevForGuard?.format === "scaffold" ? prevForGuard.files : {};
        const mergedFiles = { ...previousFiles, ...extractedFiles };
        // Log if this looks like a partial set with no previous scaffold to merge with
        if (!previousFiles["routes/data.js"] && !mergedFiles["routes/data.js"]) {
          console.warn("[streamAndDeploy] No data route in merged files — using backend defaults");
        }
        payload = { format: "scaffold", files: mergedFiles };
      } else if (extractedCode && !isScaffoldApp) {
        payload = { format: "monolithic", code: extractedCode };
      } else if (extractedCode && isScaffoldApp) {
        // Backend returned monolithic code for a scaffold app — ignore it.
        console.warn(
          "[streamAndDeploy] Ignoring monolithic extractedCode for scaffold app — " +
          "probably a false-positive extraction from an instructional code block."
        );
        payload = null;
      } else {
        payload = null;
      }

      // Handle empty response from Claude
      if (!fullText && !hasCode) {
        setMessages((prev) => {
          const u = [...prev];
          u[u.length - 1] = { role: "assistant", content: "I wasn't able to generate a response. This can happen when the request is too complex or the AI service is busy. Please try again." };
          return u;
        });
        return;
      }

      if (hasCode && !skipPreview) {
        // Show deploying state
        setMessages((prev) => {
          const u = [...prev];
          u[u.length - 1] = { role: "assistant", content: fullText, _hasCode: true, _deploying: true, _layerBadge: layer };
          return u;
        });

        // Auto-deploy preview — send merged files (not just new ones) or code.
        // Include summary + userPrompt so the backend can auto-snapshot this
        // version for the revert history. Auto-fix passes get skipSnapshot=true
        // so we don't pollute history with repair iterations; the user can
        // only revert to states they explicitly requested.
        const appName = integrationNameRef.current || "preview";
        const deployBody = payload.format === "scaffold"
          ? {
              name: appName,
              files: payload.files,
              summary: changeSummary || undefined,
              userPrompt: userPrompt || undefined,
              skipSnapshot: !!isAutoFix,
              iterationId: iterationId || undefined,
            }
          : { name: appName, code: payload.code || extractedCode, iterationId: iterationId || undefined };
        // Track whether a branch below has already finalized the assistant
        // message, so the unconditional Finalize at the bottom doesn't
        // overwrite a freshly-appended system error (which caused the
        // "duplicate response" bug the user reported).
        let messageFinalized = false;
        try {
          const deployRes = await authFetch(`${API_BASE}/deploy/preview`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(deployBody),
          });
          const deployData = await deployRes.json();

          if (deployRes.ok) {
            setPreviewUrl(localizePreviewUrl(deployData.url));
            setPendingPayload(payload); setIsSaved(false);
            setTimeout(() => { if (previewRef.current) previewRef.current.reload(); }, PREVIEW_RELOAD_DELAY);

            // Auto-validate after preview loads — check data, errors, secrets
            if (validateAndAutoFixRef.current) validateAndAutoFixRef.current(appName, payload);
          } else if (deployData.structuralError) {
            // Structural error — Claude emitted a stub / partial file that
            // would have wiped the working file. Critically, we do NOT
            // update pendingPayload here — we KEEP the old working state
            // so the user's next edit sees the real code in context,
            // not the stub. This prevents the cascade where accepting a
            // broken state corrupts all subsequent edits.
            console.warn("[deploy] Structural error (working state preserved):", deployData.error);
            setMessages((prev) => {
              const u = [...prev];
              u[u.length - 1] = { role: "assistant", content: fullText, _hasCode: true, _layerBadge: layer };
              u.push({
                role: "system",
                content:
                  `I didn't deploy that — Claude's output was incomplete (${deployData.error}). ` +
                  `Your working app is still intact. Try saying "regenerate the full file" or give me more specific guidance.`,
              });
              return u;
            });
            messageFinalized = true;
          } else if (deployData.syntaxError) {
            // Syntax error — show error, let user decide to fix.
            // Finalize the assistant message (clear _deploying) AND append the
            // system error in a SINGLE setter call, then mark messageFinalized
            // so the unconditional Finalize block below doesn't clobber it.
            console.warn("[deploy] Syntax error:", deployData.error);
            setPendingPayload(payload); setIsSaved(false); // Keep so user can try again
            setMessages((prev) => {
              const u = [...prev];
              u[u.length - 1] = { role: "assistant", content: fullText, _hasCode: true, _layerBadge: layer };
              u.push({ role: "system", content: `There's a syntax issue in the generated code. Try saying "fix the error" and I'll regenerate it.` });
              return u;
            });
            messageFinalized = true;
          } else {
            setPendingPayload(payload); setIsSaved(false);
          }
        } catch {
          setPendingPayload(payload);
        }

        // Finalize message — skipped when a branch above already handled it
        // (e.g., syntax-error path appends a system msg that we don't want
        // overwritten with a duplicate of the assistant content).
        if (!messageFinalized) {
          setMessages((prev) => {
            const u = [...prev];
            u[u.length - 1] = { role: "assistant", content: fullText, _hasCode: true, _layerBadge: layer };
            return u;
          });
        }
      } else if (hasCode) {
        // Code generated but skip preview (e.g., not needed)
        setPendingPayload(payload);
        setMessages((prev) => {
          const u = [...prev];
          u[u.length - 1] = { role: "assistant", content: fullText, _hasCode: true, _layerBadge: layer };
          return u;
        });
      } else {
        // No code — just a text response. Tagging with _layerBadge when layer
        // is set makes misroutes visible: a user asking for a visual tweak and
        // getting back a text-only "I'll note that for the next pass" response
        // now carries a clear "Data" or "Design" badge so everyone can see
        // which scope produced the reply.
        setMessages((prev) => {
          const u = [...prev];
          u[u.length - 1] = { role: "assistant", content: fullText, _layerBadge: layer };
          return u;
        });
      }
    } catch (err) {
      // Always clear streaming flag on error
      setMessages((prev) => {
        const u = [...prev];
        if (u.length > 0 && u[u.length - 1]._streaming) {
          u[u.length - 1] = { role: "assistant", content: `Something went wrong: ${err.message}` };
        } else {
          u.push({ role: "assistant", content: `Something went wrong: ${err.message}` });
        }
        return u;
      });
    }
  }, [sessionId]);

  const sendMessage = useCallback(async (fileAttachment) => {
    const text = input.trim();
    if (!text && !fileAttachment) return;
    if (loading) return;

    const displayText = fileAttachment
      ? `${fileAttachment.type === "image" ? "🖼" : "📄"} ${fileAttachment.filename}${text ? "\n" + text : ""}`
      : text;
    const userMsg = { role: "user", content: text || `Attached: ${fileAttachment?.filename}`, _displayContent: displayText };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput("");
    setLoading(true);

    // Reset self-healing counter on each new user message. Also clear the
    // dedupe signature — a new user turn is a legitimately new attempt, so
    // we shouldn't treat its first validation as "already seen".
    setHealIterations(0);
    lastAutoFixSignatureRef.current = null;

    try {
      // Revert waterline + context trimming, delegated to the top-level
      // helper so the logic has a testable seam (see regression test
      // 2026-04-22_revert-waterline.test.js).
      const history = buildHistoryWithWaterline(updatedMessages);

      const isEdit = !!integrationNameRef.current;
      let messageText = text || `Create a dashboard from the attached ${fileAttachment?.type || "file"}`;

      // Inject current code context for edits so Claude can see existing design
      if (isEdit && pendingPayloadRef.current) {
        const codeCtx = buildCodeContext(pendingPayloadRef.current);
        if (codeCtx) {
          const fileList = pendingPayloadRef.current.format === "scaffold" && pendingPayloadRef.current.files
            ? Object.keys(pendingPayloadRef.current.files).join(", ")
            : "server.js";
          messageText = `CURRENT APP CODE — these files are already deployed and working (${fileList}):\n\n${codeCtx}\n\n` +
            `EDITING RULES:\n` +
            `- ONLY output the file(s) that need to change to fulfill the request below\n` +
            `- DO NOT regenerate files that don't need changes — unchanged files are preserved automatically\n` +
            `- PRESERVE the existing visual design (colors, layout, fonts, animations) unless explicitly asked to change it\n` +
            `- If only the data/backend logic needs to change, output ONLY [FILE: routes/data.js]\n` +
            `- If only the visual/HTML needs to change, output ONLY [FILE: views/index.html]\n\n` +
            `USER REQUEST: ${messageText}`;
        }
      }

      // ── Plan-first creation: detect complex new app prompts ──
      if (!isEdit && !fileAttachment && isComplexCreation(messageText)) {
        try {
          // Stream the plan from the plan endpoint
          setMessages((prev) => [...prev, { role: "assistant", content: "", _streaming: true }]);

          const planResult = await streamChat({
            sessionId,
            message: messageText,
            history,  // Use full conversation history (includes Q&A)
            endpoint: "/smart-edit/plan",
            onText: (text) => {
              setMessages((prev) => {
                const u = [...prev];
                u[u.length - 1] = { role: "assistant", content: text, _streaming: true };
                return u;
              });
            },
            onName: (name) => {
              assignAppName(name);
            },
          });

          // Recipe suggestion short-circuit (plan path)
          if (planResult.recipeSuggestion) {
            setMessages((prev) => {
              const u = [...prev];
              u[u.length - 1] = {
                role: "system",
                content: "",
                _isRecipeSuggestion: true,
                _recipeMatch: planResult.recipeSuggestion,
                _originalMessage: messageText,
                _originalHistory: history,
                _originalIsEdit: false,
              };
              return u;
            });
            setLoading(false);
            return;
          }

          const { fullText: planText, plan } = planResult;

          // Show the plan text
          setMessages((prev) => {
            const u = [...prev];
            u[u.length - 1] = { role: "assistant", content: planText };
            return u;
          });

          if (plan) {
            setCurrentPlan(plan);

            // Extract design questions from the AI's response
            const designQuestions = extractQuestions(planText);

            if (designQuestions.length > 0) {
              // AI is asking questions — show structured question card
              // Strip the question portion from the displayed text to avoid duplication
              setMessages((prev) => [...prev, {
                role: "system",
                content: "",
                _isDesignQuestions: true,
                _designQuestions: designQuestions,
                _designPlan: plan,
                _designOriginalMessage: messages.find((m) => m.role === "user")?.content || messageText,
              }]);
            } else {
              // No questions — show BuildPlanCard directly
              const originalMsg = messages.find((m) => m.role === "user")?.content || messageText;
              setMessages((prev) => [...prev, {
                role: "system",
                content: "",
                _isBuildPlan: true,
                _buildPlan: plan,
                _buildPlanOriginalMessage: originalMsg,
              }]);
            }

            setLoading(false);
            return;
          }
          // If no structured plan was extracted, fall through to normal generation
        } catch (err) {
          console.warn("[planBuild] Plan generation failed, falling back to standard flow:", err.message);
          // Fall through to standard creation
        }
      }

      // ── Preserve Design: if user says "don't change the design", route data-only ──
      if (isEdit && !fileAttachment && wantsToPreserveDesign(messageText)) {
        await streamAndDeploy({
          message: messageText,
          history,
          isEdit: true,
          layer: "data", // Force data-only — design layer is untouchable
          userPrompt: text,
        });
        setLoading(false);
        return;
      }

      // ── Smart Edit: decompose multi-layer edits ──
      // IMPORTANT: pass the ORIGINAL user text (not messageText) — messageText has
      // been mutated above to prepend the full app source + EDITING RULES, which
      // (a) always trips shouldDecompose's data+design indicators, and (b) causes
      // Haiku's decompose endpoint to echo source code instead of returning JSON.
      if (isEdit && !fileAttachment && shouldDecompose(text)) {
        try {
          const decomposition = await decomposeEdit(
            text,
            integrationNameRef.current,
            pendingPayload?.files ? Object.keys(pendingPayload.files) : []
          );

          if (!decomposition.isSingleLayer && decomposition.passes.length > 1) {
            // Multi-layer edit: show fix plan for approval.
            // Store BOTH shapes: clean `text` for executeSmartEdit (which prepends
            // its own code context per-pass) and fat `messageText` for the skip
            // path (streamAndDeploy, which expects code baked into the message).
            // Conflating them caused the message to exceed the 50K zod limit
            // because the app code ended up embedded twice.
            setMessages((prev) => [
              ...prev,
              {
                role: "system",
                content: "", // Rendered by FixPlanCard component
                _isFixPlan: true,
                _fixPlanPasses: decomposition.passes,
                _fixPlanOriginalMessage: text,          // clean — for executeSmartEdit
                _fixPlanFullMessage: messageText,       // fat — for skip-plan streamAndDeploy
                _fixPlanHistory: history,
                _fixPlanExecuting: false,
                _fixPlanCurrentPass: -1,
              },
            ]);
            setPendingFixPlan({ passes: decomposition.passes, originalMessage: text, history });
            setLoading(false);
            return; // Wait for user to approve via FixPlanCard
          } else if (decomposition.isSingleLayer && decomposition.passes.length === 1) {
            // Single layer: route directly with layer scoping (no confirmation needed).
            // For design passes, rebuild messageText with a trimmed data-layer
            // context (just the sample JSON shape, not the full routes/data.js
            // implementation) so Claude Opus 4.7 has enough token budget to
            // actually generate the HTML. Rob's apple-music session had double
            // empty responses because thinking + full context ate the budget.
            const pass = decomposition.passes[0];
            let scopedMessage = messageText;
            if (pass.layer === "design" && pendingPayloadRef.current) {
              let sampleData = null;
              try {
                const inferred = await inferContract(integrationNameRef.current);
                sampleData = inferred.contract?.sampleData || null;
              } catch {
                // If inferContract fails, fall through to the fat messageText.
                // Not a regression — we were always sending that before.
              }
              if (sampleData) {
                const trimmedCtx = buildCodeContext(pendingPayloadRef.current, {
                  layer: "design",
                  sampleData,
                });
                if (trimmedCtx) {
                  const fileList = pendingPayloadRef.current.format === "scaffold" && pendingPayloadRef.current.files
                    ? Object.keys(pendingPayloadRef.current.files).join(", ")
                    : "server.js";
                  scopedMessage =
                    `CURRENT APP CODE — these files are already deployed and working (${fileList}):\n\n${trimmedCtx}\n\n` +
                    `EDITING RULES:\n` +
                    `- ONLY output the file(s) that need to change to fulfill the request below\n` +
                    `- DO NOT regenerate files that don't need changes — unchanged files are preserved automatically\n` +
                    `- PRESERVE the existing visual design (colors, layout, fonts, animations) unless explicitly asked to change it\n` +
                    `- If only the visual/HTML needs to change, output ONLY [FILE: views/index.html]\n\n` +
                    `USER REQUEST: ${text}`;
                }
              }
            }
            await streamAndDeploy({
              message: scopedMessage,
              history,
              isEdit: true,
              layer: pass.layer,
              userPrompt: text,
            });
            setLoading(false);
            return;
          }
          // Fall through to normal flow if decomposition returned no passes
        } catch (err) {
          console.warn("[smartEdit] Decomposition failed, falling back to standard edit:", err.message);
          // Fall through to standard edit
        }
      }

      await streamAndDeploy({
        message: messageText,
        history,
        attachment: fileAttachment,
        isEdit,
        userPrompt: text,
      });
    } catch (err) {
      // Ensure error is visible even if streamAndDeploy's catch didn't fire
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, streamAndDeploy, pendingPayload]);

  // Send a specific message directly (used by template gallery)
  // Send a specific message directly (used by template gallery)
  const sendDirect = useCallback((text) => {
    if (!text || loading) return;
    setInput("");
    const userMsg = { role: "user", content: text };
    setMessages([userMsg]);
    setLoading(true);

    const history = [{ role: "user", content: text }];
    streamAndDeploy({ message: text, history, userPrompt: text }).finally(() => setLoading(false));
  }, [loading, streamAndDeploy]);

  // Auto-fix: accept validation result OR legacy (errorMsg, errorLogs, payload) args
  const autoFix = useCallback(async (name, validationOrErrorMsg, payloadOrErrorLogs, legacyPayload) => {
    setLoading(true);

    // Determine if called with new validation result or legacy args
    const isValidation = validationOrErrorMsg && typeof validationOrErrorMsg === "object" && "issues" in validationOrErrorMsg;
    const validation = isValidation ? validationOrErrorMsg : null;
    const currentPayload = isValidation ? payloadOrErrorLogs : legacyPayload;

    // Build code context from payload
    const codeContext = buildCodeContext(currentPayload)
      || `\`\`\`javascript\n${currentPayload?.code || currentPayload || ""}\n\`\`\``;

    let fixPrompt;
    if (validation) {
      // Rich diagnostic prompt from validation endpoint
      const sections = [`DIAGNOSTIC REPORT for "${name}":\n`];

      // Data endpoint
      if (validation.dataEndpoint) {
        const de = validation.dataEndpoint;
        sections.push(`1. DATA ENDPOINT (/data):`);
        sections.push(`   Status: ${de.status || "unknown"}`);
        if (de.error) sections.push(`   Error: ${de.error}`);
        if (de.sample) sections.push(`   Response (sample): ${de.sample.slice(0, 2000)}`);
        sections.push("");
      }

      // Runtime errors
      if (validation.runtimeErrors && validation.runtimeErrors.length > 0) {
        sections.push(`2. RUNTIME ERRORS:`);
        validation.runtimeErrors.slice(0, 5).forEach((e) => {
          sections.push(`   - ${e.message} (line ${e.lineno})`);
          if (e.stack) sections.push(`     Stack: ${e.stack.slice(0, 300)}`);
        });
        sections.push("");
      }

      // Container logs
      if (validation.containerLogs) {
        sections.push(`3. CONTAINER LOGS (recent):\n\`\`\`\n${validation.containerLogs.slice(-1500)}\n\`\`\``);
        sections.push("");
      }

      // Secret mismatches
      if (validation.secretMismatch) {
        const sm = validation.secretMismatch;
        sections.push(`4. SECRET KEY STATUS:`);
        sections.push(`   Code uses: ${sm.codeUses.join(", ")}`);
        sections.push(`   Configured: ${sm.configured.join(", ") || "none"}`);
        if (sm.caseMismatches?.length > 0) sections.push(`   Case mismatches: ${sm.caseMismatches.join(", ")} — update the code to match the configured key names`);
        sections.push("");
      }

      // Classifier hint — tells Claude what kind of problem this is (code vs config vs auth)
      if (validation.diagnosis) {
        const d = validation.diagnosis;
        sections.push(`5. ERROR CLASSIFICATION (from heuristic analysis, use as a hint):`);
        sections.push(`   Category: ${d.category} (confidence: ${d.confidence})`);
        if (d.detectedErrorCodes?.length) sections.push(`   Detected error codes: ${d.detectedErrorCodes.join(", ")}`);
        sections.push(`   Reason: ${d.reason}`);
        sections.push(`   Suggestion: ${d.suggestedAction}`);
        sections.push("");
      }

      sections.push(`6. CURRENT CODE:\n${codeContext}`);
      sections.push(`\nFix the issues above. Only output the files that need to change.`);
      sections.push(`IMPORTANT: Preserve all existing visual features (layout, images, colors, animations).`);

      // Hard guardrails — these same constraints appear in the main system
      // prompt, but auto-fix has a strong "rewrite to make it robust" bias
      // when it sees 5xx diagnostics, so we repeat the critical ones here
      // where they can't be missed. Rob's Now-Playing session regressed
      // because auto-fix turned routes/data.js into a POST handler to
      // "fix" a 502 that was actually the user's tunnel being down.
      sections.push("");
      sections.push(`━━━━━━ AUTO-FIX GUARDRAILS (read before rewriting anything) ━━━━━━`);
      sections.push(`• DO NOT put router.post() / app.post() in routes/data.js — that file is mounted at GET /data only. If the app genuinely needs to RECEIVE data pushes, that belongs in routes/webhooks/<path>.js (see the main prompt's DATA FLOW section — the module contract is \`module.exports = { handler(req, body, store) {...} }\`). Don't scramble data.js into a POST receiver; the scaffold has a proper place for push receivers.`);
      sections.push(`• If the diagnostic shows HTTP 5xx / ECONNREFUSED / ENOTFOUND / "fetch failed" against an EXTERNAL URL, the upstream is down — NOT your code. Do NOT rewrite routes/data.js to "be more robust". Tell the user to check their upstream (tunnel still up? URL still correct? API key still valid?) and emit no file changes this turn.`);
      sections.push(`• NEVER emit a partial file. Every [FILE: path] block must contain the ENTIRE file content including unchanged sections. Emitting a stub or a comment placeholder wipes the working file. If you only need to change 5 lines out of 500, you still output all 500 lines (modified).`);

      fixPrompt = sections.join("\n");
    } else {
      // Legacy prompt format (manual auto-fix button)
      const errorMsg = validationOrErrorMsg;
      const errorLogs = payloadOrErrorLogs;
      fixPrompt = [
        `My integration "${name}" has an error after deployment.`,
        ``,
        `Error: ${errorMsg}`,
        errorLogs ? `\nContainer logs:\n\`\`\`\n${String(errorLogs).slice(-2000)}\n\`\`\`` : "",
        ``,
        `Here is the current code:`,
        codeContext,
        ``,
        `Please fix the error and generate the corrected code.`,
        `IMPORTANT: Preserve all existing visual features.`,
      ].join("\n");
    }

    // If screenshot available from validation, include it as multimodal content for Claude vision
    let messageContent = fixPrompt;
    if (validation?.screenshot) {
      // Build multimodal content: text + image
      const screenshotData = validation.screenshot.replace(/^data:image\/\w+;base64,/, "");
      const mimeType = validation.screenshot.startsWith("data:image/png") ? "image/png" : "image/jpeg";
      messageContent = [
        { type: "text", text: fixPrompt + "\n\n7. SCREENSHOT of current dashboard state (attached below):" },
        { type: "image", source: { type: "base64", media_type: mimeType, data: screenshotData } },
      ];
    }

    const fixMsg = {
      role: "user",
      content: messageContent,
      _displayContent: `Detected an issue — auto-fixing **${name}**...`,
      _isAutoFix: true,
    };
    setMessages((prev) => [...prev, fixMsg]);

    try {
      const history = [{ role: "user", content: messageContent }];
      await streamAndDeploy({ message: typeof messageContent === "string" ? messageContent : fixPrompt, history, isAutoFix: true });
    } finally {
      setLoading(false);
    }
  }, [streamAndDeploy]);

  /**
   * Shared helper: validate a deployed preview and auto-fix issues.
   * Used by streamAndDeploy, executePlanBuild, and executeSmartEdit after deploying.
   * Must be defined after autoFix to avoid circular reference.
   */
  const validateAndAutoFix = useCallback((appName, payload) => {
    setTimeout(async () => {
      try {
        setMessages((prev) => [...prev, { role: "system", content: "Validating your dashboard...", _validating: true }]);

        const valRes = await authFetch(`${API_BASE}/deploy/validate/${appName}-preview`, { method: "POST" });
        const validation = await valRes.json();

        if (validation.issues && validation.issues.length > 0) {
          // Use the backend's structured diagnosis if available (new path — post Phase 1).
          // Falls back to substring heuristics if the backend is older and didn't return one.
          const diagnosis = validation.diagnosis;

          // Signature dedupe — if the current validation is identical to the
          // one we just auto-fixed against, another round won't help. This
          // caught Rob's Now-Playing session where 4 consecutive auto-fixes
          // fired against the same transient UNHEALTHY signal.
          const currentSignature = computeValidationSignature(validation);
          const isRepeatedSignature =
            healIterationsRef.current > 0 &&
            lastAutoFixSignatureRef.current === currentSignature;

          if (diagnosis) {
            // ── New path: gate on the classifier's decision ──
            if (!diagnosis.autoFixable) {
              // This isn't a code problem — show the user exactly what to fix and stop.
              // No iterations wasted on Azure permissions, expired OAuth tokens, rate limits, etc.
              setMessages((prev) => {
                const updated = [...prev];
                const idx = updated.findLastIndex((m) => m._validating);
                if (idx >= 0) updated[idx] = {
                  role: "system",
                  content: diagnosis.userMessage,
                  _diagnosis: diagnosis, // preserve for potential UI use later
                };
                return updated;
              });
            } else if (isRepeatedSignature) {
              // Previous auto-fix produced zero observable progress. Don't spin
              // on the same signal — surface it to the user and stop.
              setMessages((prev) => {
                const updated = [...prev];
                const idx = updated.findLastIndex((m) => m._validating);
                if (idx >= 0) updated[idx] = {
                  role: "system",
                  content:
                    `The same issue reappeared after my last attempt, so I'm going to stop auto-fixing — ` +
                    `another round will likely produce the same result. This is often a sign the problem ` +
                    `is outside the code (container warmup, upstream API, or a misconfigured setting).\n\n` +
                    `Diagnosis: ${diagnosis.userMessage}`,
                  _diagnosis: diagnosis,
                };
                return updated;
              });
            } else if (healIterationsRef.current < MAX_HEAL_ITERATIONS) {
              // Classifier says it's auto-fixable — run auto-fix.
              const iteration = healIterationsRef.current + 1;
              setHealIterations(iteration);
              lastAutoFixSignatureRef.current = currentSignature;
              setMessages((prev) => {
                const updated = [...prev];
                const idx = updated.findLastIndex((m) => m._validating);
                if (idx >= 0) updated[idx] = {
                  role: "system",
                  content: `Found an issue (${diagnosis.category}). Auto-fixing (${iteration}/${MAX_HEAL_ITERATIONS})...`,
                };
                return updated;
              });
              autoFix(appName, validation, payload);
            } else {
              // Hit max iterations — escalate to the user with concrete details.
              setMessages((prev) => {
                const updated = [...prev];
                const idx = updated.findLastIndex((m) => m._validating);
                if (idx >= 0) updated[idx] = {
                  role: "system",
                  content: `I tried ${MAX_HEAL_ITERATIONS} times but this issue persists: ${diagnosis.userMessage}\n\nCan you describe what you see or share a screenshot?`,
                };
                return updated;
              });
            }
          } else {
            // ── Fallback: backend didn't return a diagnosis (e.g. older backend) ──
            // Keep the existing substring heuristics so the UI still works during rollout.
            const missingKeys = validation.secretMismatch?.missing || [];
            const requiredKeys = validation.secretMismatch?.codeUses || [];
            const autoFixableIssues = validation.issues.filter((i) => !i.includes("Missing API keys") && !i.includes("Key name case mismatch"));

            if (missingKeys.length > 0) {
              setMessages((prev) => {
                const updated = [...prev];
                const idx = updated.findLastIndex((m) => m._validating);
                if (idx >= 0) updated[idx] = {
                  role: "system",
                  content: `Your dashboard needs API keys configured. Go to **API Keys** and add: **${missingKeys.join(", ")}**. Then say "try again" and I'll redeploy.`,
                };
                return updated;
              });
            } else if (requiredKeys.length > 0 && validation.secretMismatch?.configured?.length === 0) {
              setMessages((prev) => {
                const updated = [...prev];
                const idx = updated.findLastIndex((m) => m._validating);
                if (idx >= 0) updated[idx] = {
                  role: "system",
                  content: `Your dashboard needs API keys configured. Go to **API Keys** and add: **${requiredKeys.join(", ")}**. Then say "try again" and I'll redeploy.`,
                };
                return updated;
              });
            } else if (autoFixableIssues.length > 0 && isRepeatedSignature) {
              // Same signal as our last attempt — skip instead of spinning.
              setMessages((prev) => {
                const updated = [...prev];
                const idx = updated.findLastIndex((m) => m._validating);
                if (idx >= 0) updated[idx] = {
                  role: "system",
                  content:
                    `The same issue reappeared after my last attempt, so I'm going to stop auto-fixing. ` +
                    `This is usually a sign the problem is outside the code (container warmup, upstream API, config). ` +
                    `Remaining issue(s):\n${autoFixableIssues.map((i) => `• ${i}`).join("\n")}`,
                };
                return updated;
              });
            } else if (autoFixableIssues.length > 0 && healIterationsRef.current < MAX_HEAL_ITERATIONS) {
              const iteration = healIterationsRef.current + 1;
              setHealIterations(iteration);
              lastAutoFixSignatureRef.current = currentSignature;
              setMessages((prev) => {
                const updated = [...prev];
                const idx = updated.findLastIndex((m) => m._validating);
                if (idx >= 0) updated[idx] = {
                  role: "system",
                  content: `Found ${autoFixableIssues.length} issue(s). Auto-fixing (${iteration}/${MAX_HEAL_ITERATIONS})...`,
                };
                return updated;
              });
              autoFix(appName, validation, payload);
            } else if (autoFixableIssues.length > 0) {
              setMessages((prev) => {
                const updated = [...prev];
                const idx = updated.findLastIndex((m) => m._validating);
                if (idx >= 0) updated[idx] = {
                  role: "system",
                  content: `I tried ${MAX_HEAL_ITERATIONS} times but these issues persist:\n${autoFixableIssues.map((i) => `• ${i}`).join("\n")}\nCan you describe what you see or share a screenshot?`,
                };
                return updated;
              });
            } else {
              setMessages((prev) => prev.filter((m) => !m._validating));
            }
          }
        } else {
          setMessages((prev) => {
            const updated = [...prev];
            const idx = updated.findLastIndex((m) => m._validating);
            if (idx >= 0) {
              const healCount = healIterationsRef.current;
              updated[idx] = {
                role: "system",
                content: healCount > 0
                  ? `Dashboard is working! Auto-fixed ${healCount} issue(s) along the way.`
                  : "Dashboard is looking good! Check the preview on the right.",
              };
            }
            return updated;
          });
        }
      } catch (err) {
        console.warn("[validate] Validation check failed:", err.message);
        setMessages((prev) => prev.filter((m) => !m._validating));
      }
    }, ERROR_CHECK_DELAY);
  }, [autoFix]);
  // Keep ref in sync so early-defined functions (streamAndDeploy) can call it
  useEffect(() => { validateAndAutoFixRef.current = validateAndAutoFix; }, [validateAndAutoFix]);

  // ── Smart Edit: layer-scoped sequential execution ────────────────────
  // Pending fix plan awaiting user approval
  const [pendingFixPlan, setPendingFixPlan] = useState(null);

  /**
   * Execute a multi-layer edit as sequential, layer-scoped passes.
   * Called when user approves the FixPlanCard.
   */
  const executeSmartEdit = useCallback(async (passes, originalMessage, originalHistory) => {
    setLoading(true);
    setPendingFixPlan(null);

    // Infer contract from running app if available
    let contract = null;
    if (integrationNameRef.current) {
      try {
        const result = await inferContract(integrationNameRef.current);
        contract = result.contract;
      } catch {
        console.warn("[smartEdit] Could not infer contract, proceeding without");
      }
    }

    // Accumulate per-pass change summaries so the final snapshot can record
    // the combined effect (e.g., "Removed Kalshi + Fixed flag images") rather
    // than just whichever pass happened to run last.
    const passSummaries = [];

    try {
      for (let i = 0; i < passes.length; i++) {
        const pass = passes[i];

        // Update progress in the fix plan message
        setMessages((prev) => {
          const updated = [...prev];
          const planIdx = updated.findLastIndex((m) => m._isFixPlan);
          if (planIdx >= 0) {
            updated[planIdx] = {
              ...updated[planIdx],
              _fixPlanExecuting: true,
              _fixPlanCurrentPass: i,
            };
          }
          return updated;
        });

        // Build the focused message for this pass, with current code context.
        // For the design pass, swap the full routes/data.js for a compact
        // data-shape block so we don't blow Claude's context budget on
        // fetch plumbing the design layer won't touch anyway.
        let passMessage = buildPassMessage(pass, originalMessage);
        const codeCtx = buildCodeContext(pendingPayloadRef.current, {
          layer: pass.layer,
          sampleData: contract?.sampleData,
        });
        if (codeCtx) {
          passMessage = `CURRENT APP CODE:\n\n${codeCtx}\n\n${passMessage}`;
        }

        // Add streaming placeholder
        setMessages((prev) => [...prev, { role: "assistant", content: "", _streaming: true }]);

        // Stream with layer scoping
        const result = await streamChat({
          sessionId,
          message: passMessage,
          history: originalHistory,
          integrationName: integrationNameRef.current,
          isEdit: true,
          layer: pass.layer,
          dataContract: contract?.schema,
          sampleData: contract?.sampleData,
          onText: (text) => {
            setMessages((prev) => {
              const u = [...prev];
              u[u.length - 1] = { role: "assistant", content: text, _streaming: true };
              return u;
            });
          },
        });

        const { fullText, extractedFiles, changeSummary, iterationId: passIterationId } = result;
        if (changeSummary) passSummaries.push(changeSummary);

        // Merge extracted files with existing payload
        if (extractedFiles) {
          const prev = pendingPayloadRef.current;
          const previousFiles = prev?.format === "scaffold" ? prev.files : {};
          const mergedFiles = { ...previousFiles, ...extractedFiles };
          const payload = { format: "scaffold", files: mergedFiles };
          setPendingPayload(payload);
          setIsSaved(false);

          // Deploy preview with merged files. We ALWAYS deploy (so inferContract
          // can see fresh data), but we only RELOAD the iframe after the final
          // pass — otherwise the iframe briefly shows old HTML against new data
          // (e.g., after the data pass rewrites the data shape but before the
          // design pass updates the view), which used to cause massive 404
          // floods in the console (e.g., old HTML re-deriving flag URLs from
          // team names that no longer match the new data format).
          //
          // Snapshots: only the FINAL pass snapshots. Intermediate passes are
          // broken-by-design states the user would never want to revert to,
          // and a single multi-pass edit is conceptually one iteration.
          const isFinalPass = i === passes.length - 1;
          const appName = integrationNameRef.current || "preview";
          const deployBody = { name: appName, files: mergedFiles };
          if (passIterationId) deployBody.iterationId = passIterationId;
          if (isFinalPass) {
            const combinedSummary = passSummaries.length > 0 ? passSummaries.join(" + ") : null;
            if (combinedSummary) deployBody.summary = combinedSummary;
            if (originalMessage) deployBody.userPrompt = originalMessage;
          } else {
            deployBody.skipSnapshot = true;
          }
          try {
            const deployRes = await authFetch(`${API_BASE}/deploy/preview`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(deployBody),
            });
            const deployData = await deployRes.json();
            if (deployRes.ok) {
              setPreviewUrl(localizePreviewUrl(deployData.url));
              if (isFinalPass) {
                setTimeout(() => { if (previewRef.current) previewRef.current.reload(); }, PREVIEW_RELOAD_DELAY);
              }
            }
          } catch {
            // Non-critical — preview may still work
          }

          // Update contract after data layer changes
          if (pass.layer === "data" && integrationNameRef.current) {
            try {
              const newContract = await inferContract(integrationNameRef.current);
              contract = newContract.contract;
            } catch { /* ok */ }
          }
        }

        // Finalize this pass's message
        setMessages((prev) => {
          const u = [...prev];
          u[u.length - 1] = {
            role: "assistant",
            content: fullText,
            _hasCode: !!extractedFiles,
            _layerBadge: pass.layer,
          };
          return u;
        });
      }

      // Mark fix plan as complete
      setMessages((prev) => {
        const updated = [...prev];
        const planIdx = updated.findLastIndex((m) => m._isFixPlan);
        if (planIdx >= 0) {
          updated[planIdx] = {
            ...updated[planIdx],
            _fixPlanExecuting: true,
            _fixPlanCurrentPass: passes.length, // All done
          };
        }
        return updated;
      });

      // Validate the final result
      const finalPayload = pendingPayloadRef.current;
      const finalName = integrationNameRef.current || "preview";
      validateAndAutoFix(finalName, finalPayload);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "system", content: `Smart edit failed on pass: ${err.message}` },
      ]);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  // ── Plan Build: sequential creation for complex new apps ──────────────
  // Stores the plan from the plan endpoint (dataContract, sampleData, etc.)
  const [currentPlan, setCurrentPlan] = useState(null);

  /**
   * Execute a plan-first build: data layer → deploy → infer contract → design layer → deploy.
   * Called when user approves the BuildPlanCard.
   */
  const executePlanBuild = useCallback(async (plan, originalMessage) => {
    setLoading(true);
    setCurrentPlan(plan);

    const contract = plan.dataContract || null;
    const sampleData = plan.sampleData || null;

    try {
      // ── Research step: deeply analyze each API before generating code ──
      let apiResearch = null;
      const dataSources = plan.dataSources || [];
      const hasExternalAPIs = dataSources.some((ds) =>
        ds.type === "api" || ds.description?.match(/api|fetch|endpoint|market|odds|score/i)
      );

      if (hasExternalAPIs && dataSources.length > 0) {
        setMessages((prev) => [...prev, {
          role: "system",
          content: "Researching APIs to get the integration right on the first try...",
        }]);

        try {
          // Direct SSE call to research endpoint (not via streamChat — it wraps body in chat format)
          const researchRes = await authFetch(`${API_BASE}/smart-edit/research`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              dataSources,
              sections: plan.sections || [],
              layout: plan.suggestedLayout || null,
              colorTheme: plan._userPreferences?.find((p) => /color|theme/i.test(p.question))?.answer || null,
              originalMessage,
            }),
          });

          if (!researchRes.ok) throw new Error(`Research endpoint returned ${researchRes.status}`);

          // Read SSE stream for the research result
          const reader = researchRes.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          let research = null;

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";
            for (const line of lines) {
              if (!line.startsWith("data: ")) continue;
              try {
                const event = JSON.parse(line.slice(6));
                if (event.type === "research") research = event.content;
              } catch { /* skip parse errors */ }
            }
          }

          if (research) {
            // Handle both old format (array) and new format ({ apis, design, refreshStrategy })
            apiResearch = Array.isArray(research) ? research : research.apis || [];

            // Store full research including design patterns
            if (research.design) plan._designResearch = research.design;
            if (research.refreshStrategy) plan._refreshStrategy = research.refreshStrategy;

            // Show research summary
            const apiSummary = apiResearch.map((r) =>
              `**${r.name}**: ${r.confidence === "high" ? "confirmed" : r.confidence === "medium" ? "likely correct" : "needs verification"} — \`${r.baseUrl}${r.endpoint || ""}\``
            );
            const designSummary = research.design
              ? [`**Layout**: ${research.design.cssLayout || "auto"}`, `**Flags**: ${research.design.flagSource || "auto"}`]
              : [];

            setMessages((prev) => {
              const updated = [...prev];
              const idx = updated.findLastIndex((m) => m.content?.includes("Researching"));
              if (idx >= 0) {
                updated[idx] = {
                  role: "system",
                  content: `Research complete:\n${[...apiSummary, ...designSummary].join("\n")}`,
                };
              }
              return updated;
            });
          }
        } catch (err) {
          console.warn("[planBuild] API research failed, proceeding without:", err.message);
          setMessages((prev) => prev.filter((m) => !m.content?.includes("Researching APIs")));
        }
      }

      // ── Pass 1: Generate data layer ──
      setMessages((prev) => [...prev, {
        role: "system",
        content: "Building data layer...",
        _layerBadge: "data",
      }]);

      setMessages((prev) => [...prev, { role: "assistant", content: "", _streaming: true }]);

      // Build a focused data prompt from the plan, enriched with API research
      const researchContext = apiResearch
        ? `\n\nAPI RESEARCH RESULTS (use these exact endpoints and patterns):\n${apiResearch.map((r) => [
            `### ${r.name}`,
            `Base URL: ${r.baseUrl}`,
            r.endpoint ? `Endpoint: ${r.endpoint}` : "",
            `Auth: ${r.auth === "none" ? "Free (no key needed)" : r.auth}`,
            r.fetchCode ? `Working code:\n${r.fetchCode}` : "",
            r.responseShape ? `Response: ${r.responseShape}` : "",
            r.pitfalls?.length > 0 ? `PITFALLS:\n${r.pitfalls.map((p) => `- ${p}`).join("\n")}` : "",
            r.confidence !== "high" ? `⚠️ Confidence: ${r.confidence} — verify this endpoint` : "",
          ].filter(Boolean).join("\n")).join("\n\n")}`
        : "";

      const dataMessage = [
        originalMessage,
        `\n\nFocus on the data layer. Data sources to implement:`,
        ...(plan.dataSources || []).map((ds) =>
          `- ${ds.name}: ${ds.description}${ds.requiresKey ? " (requires API key)" : " (free, no key)"}${ds.suggestedRefreshSec ? ` — refresh every ${ds.suggestedRefreshSec}s` : ""}`
        ),
        researchContext,
      ].join("\n");

      const dataResult = await streamChat({
        sessionId,
        message: dataMessage,
        history: [{ role: "user", content: dataMessage }],
        integrationName: integrationNameRef.current,
        layer: "data",
        dataContract: contract,
        onText: (text) => {
          setMessages((prev) => {
            const u = [...prev];
            u[u.length - 1] = { role: "assistant", content: text, _streaming: true };
            return u;
          });
        },
        onName: (name) => {
          assignAppName(name);
        },
      });

      const { fullText: dataText, extractedFiles: dataFiles, changeSummary: dataSummary } = dataResult;

      // Finalize data message — suppress "Preview is live" badge since design isn't done yet
      setMessages((prev) => {
        const u = [...prev];
        u[u.length - 1] = { role: "assistant", content: dataText, _hasCode: false, _layerBadge: "data", _intermediateStep: true };
        return u;
      });

      if (!dataFiles) {
        setMessages((prev) => [...prev, {
          role: "system",
          content: "Couldn't generate the data layer. Try describing the data sources more specifically.",
        }]);
        return;
      }

      // Store and deploy data files
      const currentFiles = { ...dataFiles };
      const payload = { format: "scaffold", files: currentFiles };
      setPendingPayload(payload);
      setIsSaved(false);

      const appName = integrationNameRef.current || "preview";
      try {
        // Data-only intermediate — skip snapshot. The app has no view at this
        // point, so reverting to this state would be useless.
        const deployRes = await authFetch(`${API_BASE}/deploy/preview`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: appName, files: currentFiles, skipSnapshot: true }),
        });
        const deployData = await deployRes.json();
        if (deployRes.ok) {
          setPreviewUrl(localizePreviewUrl(deployData.url));
        }
      } catch { /* non-critical */ }

      // Wait for container to start, then infer contract from live data
      await new Promise((r) => setTimeout(r, 5000));

      let liveContract = contract;
      let liveSampleData = sampleData;
      try {
        const inferred = await inferContract(appName);
        if (inferred.contract) {
          liveContract = inferred.contract.schema;
          liveSampleData = inferred.contract.sampleData;
        }
      } catch {
        console.warn("[planBuild] Could not infer contract from live data, using plan contract");
      }

      // ── Pass 2: Generate design layer ──
      setMessages((prev) => [...prev, {
        role: "system",
        content: "Building design layer...",
        _layerBadge: "design",
      }]);

      setMessages((prev) => [...prev, { role: "assistant", content: "", _streaming: true }]);

      // Build a focused design prompt from the plan, enriched with design research
      const designResearchContext = plan._designResearch
        ? `\n\nDESIGN RESEARCH (use these exact patterns):\n` + [
            plan._designResearch.cssLayout ? `CSS Layout: ${plan._designResearch.cssLayout}` : "",
            plan._designResearch.colorVars ? `Color Variables: ${plan._designResearch.colorVars}` : "",
            plan._designResearch.fontStack ? `Fonts: ${plan._designResearch.fontStack}` : "",
            plan._designResearch.flagSource ? `Flag Images: ${plan._designResearch.flagSource}` : "",
            ...(plan._designResearch.keyPatterns || []).map((p) => `Pattern: ${p}`),
          ].filter(Boolean).join("\n")
        : "";

      const designMessage = [
        originalMessage,
        `\n\nFocus on the visual design. Sections to include:`,
        ...(plan.sections || []).map((s) => `- ${s.name}: ${s.description}`),
        plan.suggestedLayout ? `\nSuggested layout: ${plan.suggestedLayout}` : "",
        designResearchContext,
      ].join("\n");

      const designResult = await streamChat({
        sessionId,
        message: designMessage,
        history: [{ role: "user", content: designMessage }],
        integrationName: integrationNameRef.current,
        layer: "design",
        dataContract: liveContract,
        sampleData: liveSampleData,
        onText: (text) => {
          setMessages((prev) => {
            const u = [...prev];
            u[u.length - 1] = { role: "assistant", content: text, _streaming: true };
            return u;
          });
        },
      });

      const { fullText: designText, extractedFiles: designFiles, changeSummary: designSummary } = designResult;

      // Finalize design message
      setMessages((prev) => {
        const u = [...prev];
        u[u.length - 1] = { role: "assistant", content: designText, _hasCode: !!designFiles, _layerBadge: "design" };
        return u;
      });

      if (designFiles) {
        // Merge data + design files
        const mergedFiles = { ...currentFiles, ...designFiles };
        const mergedPayload = { format: "scaffold", files: mergedFiles };
        setPendingPayload(mergedPayload);

        // Deploy the full app — this IS a snapshot point (the app is now
        // complete and usable). Combine data+design summaries into one label.
        const combinedSummary = [dataSummary, designSummary].filter(Boolean).join(" + ") || null;
        try {
          const deployRes = await authFetch(`${API_BASE}/deploy/preview`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: appName,
              files: mergedFiles,
              summary: combinedSummary || undefined,
              userPrompt: originalMessage || undefined,
            }),
          });
          const deployData = await deployRes.json();
          if (deployRes.ok) {
            setPreviewUrl(localizePreviewUrl(deployData.url));
            setTimeout(() => { if (previewRef.current) previewRef.current.reload(); }, PREVIEW_RELOAD_DELAY);
          }
        } catch { /* non-critical */ }
      }

      // Validate the deployed dashboard — catch data errors, JS errors, missing keys
      const finalPayload = pendingPayloadRef.current;
      const finalName = integrationNameRef.current || "preview";
      validateAndAutoFix(finalName, finalPayload);

    } catch (err) {
      setMessages((prev) => [...prev, {
        role: "system",
        content: `Build failed: ${err.message}. You can try again or describe what you want differently.`,
      }]);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  // Discard the preview — clean up container and clear pending code
  // Build APK from the current pending code
  // Build APK in background with notification
  // Build APK — start in background, notification + polling handled by useEffect
  const discard = useCallback(async () => {
    if (integrationNameRef.current) {
      authFetch(`${API_BASE}/deploy/cleanup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: integrationNameRef.current }),
      }).catch(() => {});
    }
    setPendingPayload(null);
    setPreviewUrl(null);
    setMessages((prev) => [
      ...prev,
      { role: "system", content: "Preview discarded. You can describe what you'd like instead." },
    ]);
  }, []);

  // Re-deploy current files to the preview container and re-validate.
  // Used by the "Rebuild" button shown when the app is unhealthy with no clear code cause.
  const handleRebuild = useCallback(async () => {
    if (!pendingPayloadRef.current || loading) return;
    const appName = integrationNameRef.current || "preview";
    const payload = pendingPayloadRef.current;
    setMessages((prev) => [...prev, { role: "system", content: "Rebuilding preview...", _validating: true }]);
    try {
      const body = payload.format === "scaffold"
        ? { name: appName, files: payload.files, skipSnapshot: true }
        : { name: appName, code: payload.code, skipSnapshot: true };
      const res = await authFetch(`${API_BASE}/deploy/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        setPreviewUrl(localizePreviewUrl(data.url));
        setTimeout(() => { if (previewRef.current) previewRef.current.reload(); }, PREVIEW_RELOAD_DELAY);
        if (validateAndAutoFixRef.current) validateAndAutoFixRef.current(appName, payload);
      } else {
        setMessages((prev) => {
          const updated = [...prev];
          const idx = updated.findLastIndex((m) => m._validating);
          if (idx >= 0) updated[idx] = { role: "system", content: "Rebuild failed — describe what you're seeing and I'll investigate." };
          return updated;
        });
      }
    } catch (err) {
      setMessages((prev) => {
        const updated = [...prev];
        const idx = updated.findLastIndex((m) => m._validating);
        if (idx >= 0) updated[idx] = { role: "system", content: "Rebuild failed — describe what you're seeing and I'll investigate." };
        return updated;
      });
    }
  }, [loading]);

  // Promote preview to production
  const deploy = useCallback(async () => {
    if (!pendingPayload || deploying) return;

    // Ensure the app has a name before saving — ask the user if none exists
    let appName = integrationName || integrationNameRef.current;
    if (!appName) {
      const userInput = window.prompt("Name your app (lowercase, hyphens ok):", "");
      if (!userInput || !userInput.trim()) return; // User cancelled
      appName = assignAppName(userInput.trim());
    }

    setDeploying(true);
    const currentPayload = pendingPayload;

    try {
      // Promote the preview container to production — send files or code
      const promoteBody = { name: appName };
      if (currentPayload.format === "scaffold") {
        promoteBody.files = currentPayload.files;
      } else {
        promoteBody.code = currentPayload.code;
      }
      const res = await authFetch(`${API_BASE}/deploy/promote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(promoteBody),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(extractError(data, "Deploy failed"));

      // IMPORTANT: Do NOT overwrite previewUrl with data.url (the production
      // URL). The current previewUrl points to the preview container, which
      // the backend now keeps alive after promote. Subsequent chat edits
      // deploy to that same preview container (same name), so the iframe
      // keeps showing the user's latest work. If we set it to production
      // here, edits deploy to preview but the iframe shows frozen production,
      // which confused many users who then thought edits "didn't deploy".
      //
      // The production URL (data.url) is what gets pushed to devices; users
      // don't need it for in-editor viewing.
      setIsSaved(true);
      // NOTE: Do NOT clear pendingPayload here — it holds the full file set
      // needed for merging future partial edits from Claude.
      // Update version from promote response
      if (data.version != null) setAppVersion(data.version);

      setMessages((prev) => [
        ...prev,
        {
          role: "system",
          content: "Saving your app...",
          _checking: true,
        },
      ]);

      setTimeout(() => {
        if (previewRef.current) previewRef.current.reload();
      }, 2000);

      // Auto health check
      setTimeout(async () => {
        try {
          const hRes = await authFetch(`${API_BASE}/integrations/${appName}/health`);
          const health = await hRes.json();

          if (health.healthy) {
            setMessages((prev) => {
              const updated = [...prev];
              const checkIdx = updated.findLastIndex((m) => m._checking);
              if (checkIdx >= 0) {
                updated[checkIdx] = {
                  role: "system",
                  content: "Your app is saved! Open My Apps to publish it to devices, or keep refining here.",
                };
              }
              return updated;
            });
          } else {
            setMessages((prev) => {
              const updated = [...prev];
              const checkIdx = updated.findLastIndex((m) => m._checking);
              if (checkIdx >= 0) {
                updated[checkIdx] = {
                  role: "system",
                  content: `Issue detected: ${health.error}`,
                  _autoFixAvailable: true,
                  _fixName: appName,
                  _fixError: health.error,
                  _fixLogs: health.logs,
                  _fixPayload: currentPayload,
                };
              }
              return updated;
            });
          }
        } catch {
          setMessages((prev) => {
            const updated = [...prev];
            const checkIdx = updated.findLastIndex((m) => m._checking);
            if (checkIdx >= 0) {
              updated[checkIdx] = {
                role: "system",
                content: "Deployed! Check the preview panel to verify.",
              };
            }
            return updated;
          });
        }
      }, 5000);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "system", content: `Deploy failed: ${err.message}` },
      ]);
    } finally {
      setDeploying(false);
    }
  }, [pendingPayload, deploying, integrationName, messages, assignAppName]);

  const handleLoadConversation = useCallback(async (conv) => {
    setPage("home");
    if (conv.integrationName) {
      setIntegrationName(conv.integrationName);
      integrationNameRef.current = conv.integrationName;
    }
    if (conv.sessionId) setSessionId(conv.sessionId);

    const previewBase = process.env.REACT_APP_PREVIEW_BASE_URL || window.location.origin;
    if (conv.integrationName) {
      setPreviewUrl(`${previewBase}/apps/${conv.integrationName}`);
    }

    // Pre-populate with the restored conversation so it renders immediately.
    // We'll REPLACE message 0 (the hidden-context app-load seed) with fresh
    // code once the /code fetch below returns. Doing it in two phases keeps
    // the perceived load time snappy — the user sees their chat history
    // right away, and the under-the-hood state refreshes.
    const restored = (conv.messages || []).map((m) => {
      if (m.role === "user" && typeof m.content === "string" && m.content.includes("```javascript")) {
        return { ...m, _displayContent: `Loaded **${conv.integrationName}** for editing.` };
      }
      return m;
    });
    setMessages(restored);

    // Refresh message 0 with the CURRENT deployed code — critical for the
    // next Claude call. The saved conv has a seed message with code from
    // WHEN THE USER LAST OPENED THE APP; by now that could be hours or days
    // stale (especially after a revert — the whole reason we're fixing this).
    // Without this refresh, Claude bases its next edit on stale code and
    // can "resurrect" features that were reverted away.
    if (conv.integrationName) {
      try {
        const res = await authFetch(`${API_BASE}/integrations/${encodeURIComponent(conv.integrationName)}/code`);
        if (res.ok) {
          const data = await res.json();
          let freshSeed = null;
          if (data.format === "scaffold" && data.files) {
            const fileBlocks = Object.entries(data.files)
              .map(([p, code]) => `[FILE: ${p}]\n\`\`\`${p.endsWith(".html") ? "html" : "javascript"}\n${code}\n\`\`\``)
              .join("\n\n");
            freshSeed = {
              role: "user",
              content: [
                `I have an existing integration called "${conv.integrationName}" that is currently deployed and running.`,
                `Here are the current source files:`,
                fileBlocks,
                `I want to make changes. When I describe what I want changed, only output the files that need to change using [FILE: path] markers. Unchanged files will be preserved automatically.`,
              ].join("\n\n"),
              _displayContent: `Loaded **${conv.integrationName}** for editing.`,
            };
            setPendingPayload({ format: "scaffold", files: data.files });
            setIsSaved(true);
          } else if (data.code) {
            freshSeed = {
              role: "user",
              content: [
                `I have an existing integration called "${conv.integrationName}" that is currently deployed and running.`,
                `This is an older-format app with all code in a single file. Here is the current source code:`,
                "```javascript",
                data.code,
                "```",
                `I want to make changes to this integration.`,
              ].join("\n"),
              _displayContent: `Loaded **${conv.integrationName}** for editing.`,
            };
            setPendingPayload({ format: "monolithic", code: data.code });
            setIsSaved(true);
          }

          // Replace message 0 (the seed) with the fresh one. If no seed
          // existed in the restored conversation (unusual), prepend it.
          if (freshSeed) {
            setMessages((prev) => {
              if (prev.length === 0) return [freshSeed];
              const first = prev[0];
              const looksLikeSeed =
                first.role === "user" &&
                typeof first.content === "string" &&
                /I have an existing integration/.test(first.content);
              if (looksLikeSeed) {
                return [freshSeed, ...prev.slice(1)];
              }
              return [freshSeed, ...prev];
            });
          }
        }
      } catch (err) {
        // Non-fatal — user still has the restored conversation, we just
        // couldn't refresh the seed. Next edit might see stale code, but
        // that's no worse than the pre-fix behavior.
        console.warn("[handleLoadConversation] Failed to refresh seed code:", err?.message);
      }
      setTimeout(() => {
        if (previewRef.current) previewRef.current.reload();
      }, 500);
    }
  }, []);

  const handleSelectIntegration = useCallback((ig) => {
    setPage("home");
    setPreviewUrl(localizePreviewUrl(ig.url));
    setIntegrationName(ig.name);
    integrationNameRef.current = ig.name;
    setTimeout(() => {
      if (previewRef.current) previewRef.current.reload();
    }, 500);
  }, []);

  const handleEdit = useCallback(async (ig) => {
    setPage("home");
    setIntegrationName(ig.name);
    integrationNameRef.current = ig.name;
    setPreviewUrl(localizePreviewUrl(ig.url));
    setSessionId(uuidv4());
    setPendingPayload(null);

    // Block input + show loading state so user can't type before code is fetched
    setLoading(true);
    setMessages([{ role: "system", content: `Loading **${ig.name}**...` }]);

    try {
      const res = await authFetch(`${API_BASE}/integrations/${ig.name}/code`);
      if (!res.ok) {
        const ct = res.headers.get("content-type") || "";
        if (ct.includes("json")) {
          const err = await res.json().catch(() => ({}));
          throw new Error(extractError(err, `Server error ${res.status}`));
        }
        throw new Error(`Server error (${res.status}). The app container may be slow to respond — try again.`);
      }
      const data = await res.json();
      console.log("[handleEdit] Loaded code:", data.format, data.files ? Object.keys(data.files) : "monolithic");

      // Build the context message based on format (scaffold vs monolithic)
      let contextContent;
      if (data.format === "scaffold" && data.files) {
        const fileBlocks = Object.entries(data.files)
          .map(([path, code]) => `[FILE: ${path}]\n\`\`\`${path.endsWith(".html") ? "html" : "javascript"}\n${code}\n\`\`\``)
          .join("\n\n");
        contextContent = [
          `I have an existing integration called "${ig.name}" that is currently deployed and running at ${ig.url}.`,
          `Here are the current source files:`,
          fileBlocks,
          `I want to make changes. When I describe what I want changed, only output the files that need to change using [FILE: path] markers. Unchanged files will be preserved automatically.`,
        ].join("\n\n");
      } else {
        contextContent = [
          `I have an existing integration called "${ig.name}" that is currently deployed and running at ${ig.url}.`,
          `This is an older-format app with all code in a single file. Here is the current source code:`,
          `\`\`\`javascript`,
          data.code,
          `\`\`\``,
          `I want to make changes to this integration. Since this needs to be converted to the new scaffold format, please output ALL three files: [FILE: routes/data.js], [FILE: views/index.html], and [FILE: config.js]. Keep all existing functionality.`,
        ].join("\n");
      }

      // Store the existing files so partial edits can merge with them
      if (data.format === "scaffold" && data.files) {
        setPendingPayload({ format: "scaffold", files: data.files });
      } else if (data.code) {
        setPendingPayload({ format: "monolithic", code: data.code });
      }
      setIsSaved(true); // Already deployed, so mark as saved

      const hiddenContext = {
        role: "user",
        content: contextContent,
        _displayContent: `Loaded **${ig.name}** for editing.`,
      };

      // Check if there's a prior conversation worth resuming. The sidebar
      // History page was removed 2026-04-22 in favor of this inline entry
      // point — less noise in the nav, puts the "resume?" decision in the
      // right context. We only surface the banner if there's >2 message-
      // bearing turns (filters out empty/freshly-reset sessions). The user
      // can resume or dismiss in-line; either way they can start editing
      // immediately without clicking anything.
      let resumeBanner = null;
      try {
        const convRes = await authFetch(`${API_BASE}/chat/conversations/${encodeURIComponent(ig.name)}`);
        if (convRes.ok) {
          const conv = await convRes.json();
          // Count only user / assistant turns — system banners, the hidden
          // "Loaded for editing" context, etc. don't count as "conversation".
          const turnCount = (conv.messages || []).filter(
            (m) => (m.role === "user" || m.role === "assistant") && m.content && !m._displayContent?.startsWith("Loaded ")
          ).length;
          if (turnCount > 2) {
            resumeBanner = {
              role: "system",
              content: "",
              _isResumeBanner: true,
              _resumeConv: conv,
              _resumeTurnCount: turnCount,
              _resumeUpdatedAt: conv.updatedAt,
            };
          }
        }
      } catch {
        // Non-critical — if the API is unreachable, just don't offer the resume.
      }

      const initialMessages = [
        hiddenContext,
        {
          role: "assistant",
          content: `I've loaded your **${ig.name}** integration. You can see the live preview on the right.\n\nWhat would you like to change?`,
        },
      ];
      if (resumeBanner) initialMessages.push(resumeBanner);
      setMessages(initialMessages);

      setTimeout(() => {
        if (previewRef.current) previewRef.current.reload();
      }, 500);
    } catch (err) {
      console.error("[handleEdit] Failed to load code:", err);
      setMessages([
        {
          role: "system",
          content: `Could not load "${ig.name}" code: ${err.message}. You can still describe what the app should do and I'll build it.`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handlePublish = useCallback((integration) => {
    setPublishTarget(integration);
  }, []);

  const handleDuplicate = useCallback((integration) => {
    // Open the duplicate modal seeded with this integration as the source.
    // The modal handles the POST itself; we just react to onCreated by
    // refreshing the list + surfacing a toast-equivalent message.
    setDuplicateSource(integration);
  }, []);

  const handlePublishCurrent = useCallback(() => {
    if (!integrationName || !previewUrl) return;
    handlePublish({ name: integrationName, url: previewUrl, payload: pendingPayload });
  }, [integrationName, previewUrl, pendingPayload, handlePublish]);

  /**
   * Fetch tenants, pick or prompt for one, then fetch the user's me profile.
   * Called after a fresh Cognito sign-in AND on session restore at mount.
   */
  // forceSelector=true: always show the tenant picker (used on fresh login).
  // forceSelector=false: auto-restore saved tenant when possible (used on session restore).
  const completeLogin = useCallback(async (cognitoUser, { forceSelector = false } = {}) => {
    try {
      const session = cognitoUser.signInUserSession || (await Auth.currentSession());
      const token = session.getIdToken().getJwtToken();
      const email = session.getIdToken().payload.email;

      // Fetch tenants from the Videri CMS
      const tenantsRes = await fetch(`${API_BASE}/auth/tenants`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const tenantsData = await tenantsRes.json().catch(() => ({ tenants: [] }));
      const tenants = tenantsData.tenants || [];

      if (tenants.length === 0) {
        alert("Your Videri account does not have access to any tenants. Please contact your administrator.");
        await Auth.signOut();
        setAuthLoading(false);
        return;
      }

      setCmsTenants(tenants);

      if (!forceSelector) {
        // Session restore: auto-select if only one tenant or a saved tenant exists
        const saved = (() => { try { const s = localStorage.getItem("videri-forge-cms"); return s ? JSON.parse(s) : null; } catch { return null; } })();
        const restoredTenant = saved?.tenant ? tenants.find((t) => t.code === saved.tenant) : null;
        const autoTenant = restoredTenant || (tenants.length === 1 ? tenants[0] : null);
        if (autoTenant) {
          await applyTenant(autoTenant, token, email);
          return;
        }
      }

      // Show tenant selector — blocks rendering until selection
      setPendingTenants({ tenants, token, email });
      setAuthLoading(false);
    } catch (err) {
      console.error("[auth] completeLogin failed:", err);
      setAuthLoading(false);
    }
  }, []); // eslint-disable-line

  /** Apply a selected tenant: fetch /me, set state, persist to localStorage. */
  const applyTenant = useCallback(async (tenant, token, email) => {
    const tenantCode = typeof tenant === "string" ? tenant : tenant.code;
    try {
      const meRes = await fetch(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}`, "x-tenant": tenantCode },
      });
      const me = await meRes.json().catch(() => ({}));
      const displayName = me.name || me.display_name || email;

      const auth = { tenant: tenantCode, user: displayName };
      setCmsAuth(auth);
      localStorage.setItem("videri-forge-cms", JSON.stringify(auth));
      setForgeUser({ email, name: displayName });
    } catch (err) {
      console.error("[auth] applyTenant failed:", err);
      // Still allow access — /me is informational
      const auth = { tenant: tenantCode, user: email };
      setCmsAuth(auth);
      localStorage.setItem("videri-forge-cms", JSON.stringify(auth));
      setForgeUser({ email, name: email });
    } finally {
      setPendingTenants(null);
      setAuthLoading(false);
    }
  }, []);

  /** Called by TenantSelectorModal when the user picks a tenant. */
  const handleTenantSelect = useCallback((tenant) => {
    if (!pendingTenants) return;
    applyTenant(tenant, pendingTenants.token, pendingTenants.email);
  }, [pendingTenants, applyTenant]);

  /** Called by CmsLoginModal after Auth.signIn() succeeds. */
  const handleForgeLogin = useCallback((cognitoUser) => {
    completeLogin(cognitoUser, { forceSelector: true });
  }, [completeLogin]);

  const handleLogout = useCallback(async () => {
    try { await Auth.signOut(); } catch { /* ignore */ }
    setForgeUser(null);
    setCmsAuth(null);
    setCmsTenants([]);
    setPendingTenants(null);
    localStorage.removeItem("videri-forge-cms");
    localStorage.removeItem("videri-forge-env");
  }, []);

  // Clear auth state when authFetch detects a 401 (expired session).
  // Using an event instead of window.location.reload() stops the refresh loop:
  // reload → useIntegrations fires before auth restores → another 401 → reload.
  useEffect(() => {
    const handler = () => handleLogout();
    window.addEventListener("forge:session-expired", handler);
    return () => window.removeEventListener("forge:session-expired", handler);
  }, [handleLogout]);

  const switchTenant = useCallback(async (tenantCode) => {
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const email = session.getIdToken().payload.email;
      const tenant = cmsTenants.find((t) => t.code === tenantCode) || { code: tenantCode, name: tenantCode };
      await applyTenant(tenant, token, email);
    } catch (err) {
      console.error("[auth] switchTenant failed:", err);
    }
    resetChat();
    setPage("home");
  }, [cmsTenants, applyTenant, resetChat]);

  // Scroll lock when modals are open
  const anyModalOpen = !!publishTarget || showKeysEditor;
  useEffect(() => {
    document.body.classList.toggle("modal-open", anyModalOpen);
    return () => document.body.classList.remove("modal-open");
  }, [anyModalOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      // Ctrl/Cmd + Enter: send message (when input focused)
      // Escape: close modals
      if (e.key === "Escape") {
        if (showVersionHistory) setShowVersionHistory(false);
        else if (showVarsEditor) setShowVarsEditor(false);
        else if (showKeysEditor) setShowKeysEditor(false);
        else if (publishTarget) setPublishTarget(null);
        else if (mobileSidebar) setMobileSidebar(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [showKeysEditor, showVarsEditor, showVersionHistory, publishTarget, mobileSidebar]);

  const handleApiKeysFromSidebar = useCallback((ig) => {
    integrationNameRef.current = ig.name;
    setShowKeysEditor(true);
  }, []);

  // ── Guarded navigation: confirm before leaving active AI session ──
  const isAiBusy = loading || deploying;
  const guardedNavigate = useCallback((targetPage) => {
    if (page === "home" && isAiBusy) {
      if (!window.confirm("The AI is still working. Are you sure you want to leave? Your current progress may be lost.")) return;
    }
    setPage(targetPage);
  }, [page, isAiBusy]);

  const guardedNewApp = useCallback(() => {
    if (isAiBusy) {
      if (!window.confirm("The AI is still working. Are you sure you want to start a new app? Your current progress may be lost.")) return;
    }
    resetChat();
    setPage("home");
  }, [isAiBusy, resetChat]);

  // ── Gate: require login ─────────────────────────────────
  if (authLoading) {
    return (
      <div className="app">
        <div className="login-gate">
          <div className="login-gate-card">
            <img src="/videri-logo.png" alt="Videri" className="login-gate-logo" />
            <h1 className="login-gate-title">Forge Studio</h1>
            <p className="login-gate-subtitle">Signing in…</p>
          </div>
        </div>
      </div>
    );
  }

  if (pendingTenants) {
    return (
      <TenantSelectorModal
        tenants={pendingTenants.tenants}
        onSelect={handleTenantSelect}
      />
    );
  }

  if (!forgeUser) {
    return (
      <div className="app">
        <div className="login-gate">
          <div className="login-gate-card">
            <img src="/videri-logo.png" alt="Videri" className="login-gate-logo" />
            <h1 className="login-gate-title">Forge Studio</h1>
            <p className="login-gate-subtitle">
              Describe data integrations in plain English.<br />
              Deploy them as live dashboards on your screens.
            </p>
            <CmsLoginModal onSuccess={handleForgeLogin} onClose={() => {}} embedded />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="app-header-left">
          <button className="sidebar-hamburger" onClick={() => setMobileSidebar(true)} title="Menu">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M3 5H15M3 9H15M3 13H15" />
            </svg>
          </button>
          <div className="app-logo" onClick={guardedNewApp} role="button" tabIndex={0}>
            <img src="/videri-logo.png" alt="Videri" className="logo-img" />
            <h1>Forge Studio</h1>
          </div>
        </div>
        <div className="app-header-right">
          {/* Tenant selector */}
          {cmsAuth && (
            <div className="tenant-group">
              <span className="tenant-label">Tenant:</span>
              {cmsTenants.length > 1 ? (
                <TenantDropdown
                  tenants={cmsTenants}
                  value={cmsAuth.tenant}
                  onChange={switchTenant}
                />
              ) : (
                <span className="tenant-badge">{cmsAuth.tenant || "..."}</span>
              )}
            </div>
          )}
        </div>
      </header>
      <div className="app-body">
        <Sidebar
          open={sidebarOpen}
          onToggle={toggleSidebar}
          activePage={page}
          onNavigate={guardedNavigate}
          onNewApp={guardedNewApp}
          user={forgeUser}
          onLogout={handleLogout}
        />
        {mobileSidebar && (
          <Sidebar
            open={true}
            onToggle={() => setMobileSidebar(false)}
            activePage={page}
            onNavigate={guardedNavigate}
            onNewApp={guardedNewApp}
            user={forgeUser}
            onLogout={handleLogout}
            onClose={() => setMobileSidebar(false)}
          />
        )}
        <div className="app-content">
          {page === "home" && (
            <>
              {/* Action-gate decisions come from pure helpers in
                  lib/actionGates.js. ChatPane uses them to disable Save and
                  Publish with a specific tooltip reason instead of silent
                  grey-outs. */}
              <ChatPane
                messages={messages}
                input={input}
                onInputChange={setInput}
                onSend={sendMessage}
                loading={loading}
                pendingCode={pendingPayload}
                isSaved={isSaved}
                deploying={deploying}
                // Derived gate state — pure functions in lib/actionGates.js
                saveGate={canSave({
                  hasPendingPayload: !!pendingPayload,
                  isSaved,
                  loading,
                  deploying,
                  autoFixing: healIterations > 0 && loading,
                  validating: messages.some((m) => m._validating),
                })}
                publishGate={canPublish({
                  hasPendingPayload: !!pendingPayload,
                  isSaved,
                  loading,
                  deploying,
                  autoFixing: healIterations > 0 && loading,
                  validating: messages.some((m) => m._validating),
                })}
                onDeploy={deploy}
                onDiscard={discard}
                onPublish={previewUrl ? handlePublishCurrent : null}
                cmsConnected={true}
                integrationName={integrationName}
                onReset={resetChat}
                onAutoFix={autoFix}
                onRebuild={handleRebuild}
                onSendDirect={sendDirect}
                onConfigureKeys={() => setShowKeysEditor(true)}
                // Always show the Variables button — gating on integrationName
                // (as we used to) made Rob think the feature had disappeared
                // when he was pre-save. If clicked without a name, the modal
                // renders a "save first" prompt the same way ApiKeysEditor does.
                onConfigureVars={() => setShowVarsEditor(true)}
                onOpenHistory={integrationName ? () => setShowVersionHistory(true) : null}
                onOpenRecipe={(recipeId, docsAnchor) => {
                  // Jump to the Integrations page and auto-open the named recipe.
                  // IntegrationsList consumes pendingRecipeNav on mount and then
                  // calls back to clear it so re-navigation works later.
                  setPendingRecipeNav({ recipeId, docsAnchor });
                  setPage("integrations");
                }}
                onRecipeSuggestionUse={(msg, match) => {
                  // User accepted the matcher's recipe suggestion. Navigate to
                  // the Integrations page with that recipe pre-opened.
                  setPendingRecipeNav({ recipeId: match.recipeId });
                  setPage("integrations");
                }}
                onRecipeSuggestionContinue={(msg) => {
                  // User declined the recipe and wants Claude to handle it.
                  // Replace the suggestion card with a streaming placeholder and
                  // re-submit the same prompt with skipRecipeMatch=true so the
                  // backend falls through to normal generation.
                  setMessages((prev) => prev.filter((m) => m !== msg));
                  setLoading(true);
                  streamAndDeploy({
                    message: msg._originalMessage,
                    history: msg._originalHistory,
                    attachment: msg._originalAttachment,
                    isEdit: msg._originalIsEdit || false,
                    isAutoFix: msg._originalIsAutoFix || false,
                    layer: msg._originalLayer,
                    skipRecipeMatch: true,
                    userPrompt: msg._originalMessage,
                  }).finally(() => setLoading(false));
                }}
                appVersion={appVersion}
                onFixPlanApprove={(msg) => {
                  executeSmartEdit(msg._fixPlanPasses, msg._fixPlanOriginalMessage, msg._fixPlanHistory);
                }}
                onFixPlanSkip={(msg) => {
                  // User wants to skip decomposition — run as a standard full edit.
                  // Use the fat message (with code context baked in) since
                  // streamAndDeploy doesn't prepend code on its own here.
                  // Fall back to OriginalMessage for backwards-compat with any
                  // in-flight plan cards from before the split.
                  setPendingFixPlan(null);
                  setLoading(true);
                  streamAndDeploy({
                    message: msg._fixPlanFullMessage || msg._fixPlanOriginalMessage,
                    history: msg._fixPlanHistory,
                    isEdit: true,
                    // _fixPlanOriginalMessage holds the clean user text since we
                    // split the two forms; use it as the user-facing prompt
                    // label for the snapshot that this deploy produces.
                    userPrompt: msg._fixPlanOriginalMessage,
                  }).finally(() => setLoading(false));
                }}
                onBuildPlanApprove={(msg) => {
                  // User approved the build plan — execute sequential creation
                  executePlanBuild(msg._buildPlan, msg._buildPlanOriginalMessage);
                }}
                onBuildPlanChange={(msg) => {
                  // User wants to change the plan — let them type feedback
                  setMessages((prev) => [...prev, {
                    role: "system",
                    content: "Tell me what you'd like to change about the plan, and I'll update it.",
                  }]);
                }}
                onDesignQuestionsSubmit={(msg, answers) => {
                  // User answered all design questions — build directly with answers as context
                  const originalMessage = msg._designOriginalMessage || messages.find((m) => m.role === "user")?.content || "";
                  const plan = msg._designPlan || currentPlan;

                  // Compile answers into a context string for the AI
                  const answersText = answers.map((a) => `- ${a.question}: ${a.answer}`).join("\n");
                  const enrichedMessage = `${originalMessage}\n\nDesign preferences:\n${answersText}`;

                  // Update the plan's data with user preferences (for display)
                  if (plan) {
                    plan._userPreferences = answers;
                  }

                  // Show the BuildPlanCard with the compiled info
                  setMessages((prev) => [...prev, {
                    role: "system",
                    content: "",
                    _isBuildPlan: true,
                    _buildPlan: plan,
                    _buildPlanOriginalMessage: enrichedMessage,
                  }]);
                }}
                onResumeConversation={(msg, conv) => {
                  // User hit "Resume" on the inline previous-conversation
                  // banner. Rehydrate the full transcript via the existing
                  // loader (which sets messages, sessionId, preview URL).
                  if (conv) handleLoadConversation(conv);
                }}
                onDismissResume={(msg) => {
                  // User hit "Dismiss" — remove just this banner, keep the
                  // fresh-start chat intact. No persistent "don't show again"
                  // state; if they come back to this app, the banner returns
                  // so they can resume then.
                  setMessages((prev) => prev.filter((m) => m !== msg));
                }}
              />
              <PreviewPane ref={previewRef} url={previewUrl} appName={integrationName} />
            </>
          )}
          {page === "apps" && (
            <AppsPage
              integrations={integrations}
              loading={integrationsLoading}
              actionLoading={igActionLoading}
              onEdit={handleEdit}
              onPublish={handlePublish}
              onApiKeys={handleApiKeysFromSidebar}
              onDoAction={igDoAction}
              onSelectApp={handleSelectIntegration}
              onDuplicate={handleDuplicate}
              // Global publish gate: block while any AI work is in flight on
              // the currently-open app. The gate from canPublish also blocks
              // on an unsaved preview, but that only matters for the in-chat
              // Publish button — the AppsPage button publishes already-saved
              // containers, so we just use the AI-busy half of the signal.
              publishGate={canPublish({
                hasPendingPayload: false, // AppsPage ships from saved state
                isSaved: true,
                loading,
                deploying,
                autoFixing: healIterations > 0 && loading,
                validating: messages.some((m) => m._validating),
              })}
            />
          )}
          {page === "integrations" && (
            <IntegrationsList
              pendingNav={pendingRecipeNav}
              onPendingNavConsumed={() => setPendingRecipeNav(null)}
              onNavigateToApp={(app) => {
                // User just created an app from a recipe — land them in the
                // home view with the preview loaded. If the create flow also
                // saved their tested keys, the app is already working and the
                // welcome message reflects that; otherwise we prompt them to
                // configure API keys.
                setPage("home");
                setIntegrationName(app.name);
                integrationNameRef.current = app.name;
                setPreviewUrl(localizePreviewUrl(app.url) || null);
                setSessionId(uuidv4());
                setPendingPayload({ format: "scaffold", files: app.files || {} });
                setMessages([
                  {
                    role: "system",
                    content: app.secretsSaved
                      ? `Created **${app.name}** and saved your tested API keys — the dashboard should be loading data now. Keep refining here, or hit Publish when it's ready.`
                      : `Created **${app.name}** from the recipe! ` +
                        `Open **API Keys** in the sidebar to configure the recipe's required secrets — ` +
                        `the dashboard will start showing data as soon as they're saved.`,
                  },
                ]);
                setTimeout(() => {
                  if (previewRef.current) previewRef.current.reload();
                }, 500);
              }}
            />
          )}
          {/*
            Sidebar History route removed 2026-04-22 — entry point is now the
            inline "Resume previous conversation" banner inside the app
            editor (shown by handleEdit when a prior session has >2 messages).
            handleLoadConversation + the page mount below are preserved as a
            safety net in case we need to restore the page later.
          */}
          {page === "history" && (
            <HistoryPage onLoadConversation={handleLoadConversation} />
          )}
          {page === "devices" && (
            <DeviceDashboard cmsAuth={cmsAuth} />
          )}
          {page === "webhooks" && (
            <WebhooksPage integrations={integrations} cmsAuth={cmsAuth} />
          )}
          {page === "player" && (
            <PlayerPage cmsAuth={cmsAuth} />
          )}
          {page === "logs" && (
            <PromptLogsPage />
          )}
          {page === "usage" && (
            <UsagePage />
          )}
        </div>
      </div>
      {publishTarget && (
        <PublishWizard
          integration={publishTarget}
          cmsAuth={cmsAuth}
          onClose={() => setPublishTarget(null)}
          onPublished={() => {
            setMessages((prev) => [
              ...prev,
              { role: "system", content: `Published "${publishTarget.name}" to your devices!` },
            ]);
          }}
        />
      )}
      {duplicateSource && (
        <DuplicateAppModal
          source={duplicateSource}
          onClose={() => setDuplicateSource(null)}
          onCreated={({ name }) => {
            const srcName = duplicateSource.name;
            setDuplicateSource(null);
            // Leave a trace in the chat log so the user sees what happened —
            // a toast would vanish after a couple of seconds.
            setMessages((prev) => [
              ...prev,
              { role: "system", content: `Duplicated "${srcName}" → "${name}". Open it from My Apps.` },
            ]);
            // Refresh the Apps list so the new entry shows up immediately
            // instead of after the 30s poll.
            refetchIntegrations();
          }}
        />
      )}
      {showKeysEditor && (
        integrationNameRef.current ? (
          <ApiKeysEditor
            integrationName={integrationNameRef.current}
            onClose={() => {
              setShowKeysEditor(false);
              // Reload preview after configuring keys
              setTimeout(() => {
                if (previewRef.current) previewRef.current.reload();
              }, 500);
            }}
          />
        ) : (
          <div className="ake-overlay" onClick={() => setShowKeysEditor(false)}>
            <div className="ake-modal" onClick={(e) => e.stopPropagation()} style={{ textAlign: "center", maxWidth: 400 }}>
              <p style={{ marginBottom: 16, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>
                Save your app first so it has a name, then you can configure API keys.
              </p>
              <button onClick={() => setShowKeysEditor(false)} style={{
                background: "var(--color-accent)", color: "white", border: "none",
                borderRadius: 8, padding: "8px 24px", cursor: "pointer", fontSize: 14,
              }}>OK</button>
            </div>
          </div>
        )
      )}
      {showVarsEditor && (
        integrationNameRef.current ? (
          <AppVarsEditor
            integrationName={integrationNameRef.current}
            onClose={() => setShowVarsEditor(false)}
            onRequestRedeploy={async () => {
              // Redeploy the current files so the container picks up the
              // new env-var values. We can't just restart the container
              // (no endpoint for that, and the env vars are set at
              // create-time anyway); redeploying with the same files is
              // the simplest way to get a fresh container with the
              // updated Env array.
              const name = integrationNameRef.current;
              const payload = pendingPayloadRef.current;
              if (!name || !payload) throw new Error("No deployable payload — make a change in chat first");
              const body = payload.format === "scaffold"
                ? { name, files: payload.files, skipSnapshot: true }
                : { name, code: payload.code, skipSnapshot: true };
              const res = await authFetch(`${API_BASE}/deploy/preview`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
              });
              if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(extractError(err, `Deploy failed (${res.status})`));
              }
              const data = await res.json();
              if (data.url) setPreviewUrl(localizePreviewUrl(data.url));
              setTimeout(() => { if (previewRef.current) previewRef.current.reload(); }, PREVIEW_RELOAD_DELAY);
              setMessages((prev) => [...prev, {
                role: "system",
                content: `Redeployed with updated variables.`,
              }]);
            }}
          />
        ) : (
          <div className="ake-overlay" onClick={() => setShowVarsEditor(false)}>
            <div className="ake-modal" onClick={(e) => e.stopPropagation()} style={{ textAlign: "center", maxWidth: 400 }}>
              <p style={{ marginBottom: 16, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>
                Save your app first so it has a name, then you can set its variables.
              </p>
              <button onClick={() => setShowVarsEditor(false)} style={{
                background: "var(--color-accent)", color: "white", border: "none",
                borderRadius: 8, padding: "8px 24px", cursor: "pointer", fontSize: 14,
              }}>OK</button>
            </div>
          </div>
        )
      )}
      {showVersionHistory && integrationNameRef.current && (
        <VersionHistory
          integrationName={integrationNameRef.current}
          onClose={() => setShowVersionHistory(false)}
          onRestored={async (result) => {
            // Sync client state from the restore response. The backend now
            // includes `files` + `format` directly in the response so we
            // can populate pendingPayload WITHOUT a follow-up GET /code —
            // that fetch reads from production, which hasn't been touched
            // by the revert yet (revert writes to preview only). Using the
            // /code path gave stale pre-revert bytes and silently reverted
            // the revert on the next Save. Rob's apple-music-dashboard
            // session on 2026-04-23 reproduced this.
            //
            // If a legacy backend doesn't include files (older deploy), we
            // fall back to the /code fetch so the UI still updates — the
            // race is still possible in that branch, but only until both
            // ends are on the new version.
            if (result && result.format === "scaffold" && result.files) {
              setPendingPayload({ format: "scaffold", files: result.files });
              setIsSaved(false); // Reverted state hasn't been promoted to production
            } else {
              try {
                const appName = integrationNameRef.current;
                const codeRes = await authFetch(`${API_BASE}/integrations/${encodeURIComponent(appName)}/code`);
                if (codeRes.ok) {
                  const data = await codeRes.json();
                  if (data.format === "scaffold" && data.files) {
                    setPendingPayload({ format: "scaffold", files: data.files });
                    setIsSaved(false);
                  }
                }
              } catch { /* non-critical */ }
            }

            if (result && result.url) setPreviewUrl(localizePreviewUrl(result.url));
            setTimeout(() => { if (previewRef.current) previewRef.current.reload(); }, PREVIEW_RELOAD_DELAY);

            // Surface a chat message so the revert is visible in the transcript.
            // _isRevertMarker acts as a "waterline" — the next time history is
            // built for a Claude call, only messages AFTER this marker get
            // sent. Prevents pre-revert chat (with its stale code snapshots)
            // from leaking features back into the app. The pre-revert messages
            // stay visible in the chat UI; they just don't travel to the model.
            setMessages((prev) => [...prev, {
              role: "system",
              content: `Reverted to v${result.restoredFrom}. Saved current state as v${result.newVersion} so you can move forward again.`,
              _isRevertMarker: true,
              _revertedToVersion: result.restoredFrom,
              _newVersion: result.newVersion,
            }]);
          }}
        />
      )}
    </div>
  );
}
